import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Radio, Select, Popconfirm, notification, Badge, Spin, Dropdown, Modal, Input, Checkbox, message } from 'antd';
import { BellOutlined, EyeOutlined, MoreOutlined, HeatMapOutlined, MessageOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { database, ref, get, set, update, remove, onValue } from '../Auth/db/firebase';
import SuperAdminMessaging from '../../components/admin/SuperAdminMessaging';
import './index.css';

const { Option } = Select;

const SuperAdmin = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingViewMode, setPendingViewMode] = useState('table');
  const [approvedViewMode, setApprovedViewMode] = useState('card');
  const [todos, setTodos] = useState([]);
  const [holdModalVisible, setHoldModalVisible] = useState(false);
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [confirmHold, setConfirmHold] = useState(false);

  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  // Set page title
  useEffect(() => {
    document.title = 'Super Admin Dashboard | KioStock';
  }, []);

  const openNotification = (type, message) => {
    api[type]({
      message,
      placement: 'bottomRight',
    });
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Initial fetch for approved users (not real-time), excluding 'me'
        const approvedSnapshot = await get(ref(database, 'users'));
        const approved = [];
        if (approvedSnapshot.exists()) {
          const facilities = approvedSnapshot.val();
          for (const facilityId in facilities) {
            if (facilityId === 'me') continue; // Skip the 'me' collection
            const users = facilities[facilityId];
            for (const uid in users) {
              approved.push({ uid, facilityId, ...users[uid] });
            }
          }
        }
        setApprovedUsers(approved);

        // Load todos from localStorage
        const savedTodos = localStorage.getItem('superadmin-todos');
        if (savedTodos) {
          setTodos(JSON.parse(savedTodos));
        }
        setLoading(false);
      } catch (err) {
        openNotification('error', `Failed to load users: ${err.message}`);
        setLoading(false);
      }
    };

    // Real-time listener for pending users
    const pendingRef = ref(database, 'pending_users');
    const unsubscribe = onValue(pendingRef, (snapshot) => {
      try {
        const pending = [];
        let count = 0;
        if (snapshot.exists()) {
          const facilities = snapshot.val();
          for (const facilityId in facilities) {
            const users = facilities[facilityId];
            for (const uid in users) {
              pending.push({ uid, facilityId, ...users[uid] });
              count++;
            }
          }
        }
        setPendingUsers(pending);
        setPendingCount(count);
        if (count > 0 && !loading) {
          openNotification('info', `New pending user${count > 1 ? 's' : ''} added (${count}).`);
        }
      } catch (err) {
        openNotification('error', `Failed to load pending users: ${err.message}`);
      }
    }, (err) => {
      openNotification('error', `Real-time listener error: ${err.message}`);
    });

    fetchUsers();

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Listen for todos updates from NotificationBell
  useEffect(() => {
    const handleTodosUpdate = (event) => {
      const { todos: updatedTodos } = event.detail;
      setTodos(updatedTodos);
    };

    window.addEventListener('todosUpdated', handleTodosUpdate);
    
    return () => {
      window.removeEventListener('todosUpdated', handleTodosUpdate);
    };
  }, []);

  const handleApprove = async (user) => {
    try {
      await set(ref(database, `users/${user.facilityId}/${user.uid}`), {
        username: user.username,
        email: user.email,
        phone: user.phone,
        facilityId: user.facilityId,
        role: user.role,
        approvedAt: new Date().toISOString(),
      });
      await remove(ref(database, `pending_users/${user.facilityId}/${user.uid}`));
      setApprovedUsers([...approvedUsers, { uid: user.uid, facilityId: user.facilityId, ...user, approvedAt: new Date().toISOString() }]);
      openNotification('success', `User ${user.username} approved successfully.`);
    } catch (err) {
      openNotification('error', `Failed to approve user: ${err.message}`);
    }
  };

  const handleReject = async (user) => {
    try {
      await remove(ref(database, `pending_users/${user.facilityId}/${user.uid}`));
      openNotification('success', `User ${user.username} rejected successfully.`);
      // Note: Deleting Firebase Auth user requires server-side handling
    } catch (err) {
      openNotification('error', `Failed to reject user: ${err.message}`);
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      await remove(ref(database, `users/${user.facilityId}/${user.uid}`));
      setApprovedUsers(approvedUsers.filter((u) => u.uid !== user.uid));
      openNotification('success', `User ${user.username} deleted successfully.`);
      // Note: Deleting Firebase Auth user requires server-side handling
    } catch (err) {
      openNotification('error', `Failed to delete user: ${err.message}`);
    }
  };

  const handleChangeRole = async (user, newRole) => {
    try {
      await update(ref(database, `users/${user.facilityId}/${user.uid}`), { role: newRole });
      setApprovedUsers(
        approvedUsers.map((u) => (u.uid === user.uid ? { ...u, role: newRole } : u))
      );
      openNotification('success', `Role for ${user.username} updated to ${newRole}.`);
    } catch (err) {
      openNotification('error', `Failed to update role: ${err.message}`);
    }
  };

  const handleVisitAdminPage = (user) => {
    if (user.role === 'admin' || user.role === 'superadmin') {
      navigate(`/${user.facilityId}/admin`);
      openNotification('info', `Navigating to ${user.username}'s admin page (${user.facilityId})`);
    } else if (user.role === 'cashier') {
      navigate(`/${user.facilityId}/user`);
      openNotification('info', `Navigating to ${user.username}'s cashier page (${user.facilityId})`);
    } else {
      openNotification('warning', `No dashboard available for role: ${user.role}`);
    }
  };

  const handleCompleteTodo = (todoId) => {
    const updatedTodos = todos.map(todo =>
      todo.id === todoId ? { ...todo, status: 'completed', completedAt: new Date().toISOString() } : todo
    );
    setTodos(updatedTodos);
    localStorage.setItem('superadmin-todos', JSON.stringify(updatedTodos));
    openNotification('success', 'Todo marked as completed!');
  };

  const handleDeleteTodo = (todoId) => {
    const updatedTodos = todos.filter(todo => todo.id !== todoId);
    setTodos(updatedTodos);
    localStorage.setItem('superadmin-todos', JSON.stringify(updatedTodos));
    openNotification('info', 'Todo removed.');
  };

  const handleHoldAccount = (user) => {
    setSelectedUser(user);
    setHoldModalVisible(true);
    setConfirmHold(false);
  };

  const handleSendNotification = (user) => {
    setSelectedUser(user);
    setNotificationModalVisible(true);
    setNotificationTitle('');
    setNotificationMessage('');
  };

  const confirmHoldAccount = async () => {
    if (!confirmHold) {
      message.warning('Please confirm by checking the checkbox');
      return;
    }
    try {
      await update(ref(database, `users/${selectedUser.facilityId}/${selectedUser.uid}`), {
        accountHeld: true,
        heldAt: new Date().toISOString(),
        heldBy: 'superadmin'
      });
      setApprovedUsers(approvedUsers.map(u => 
        u.uid === selectedUser.uid ? { ...u, accountHeld: true } : u
      ));
      openNotification('success', `Account for ${selectedUser.username} has been held`);
      setHoldModalVisible(false);
    } catch (err) {
      openNotification('error', `Failed to hold account: ${err.message}`);
    }
  };

  const confirmSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      message.warning('Please fill in both title and message');
      return;
    }
    try {
      const newNotificationRef = ref(database, `notifications/user_messages/${selectedUser.facilityId}/${selectedUser.uid}/${Date.now()}`);
      await set(newNotificationRef, {
        title: notificationTitle,
        message: notificationMessage,
        from: 'superadmin',
        createdAt: new Date().toISOString(),
        read: false
      });
      openNotification('success', `Notification sent to ${selectedUser.username}`);
      setNotificationModalVisible(false);
    } catch (err) {
      openNotification('error', `Failed to send notification: ${err.message}`);
    }
  };

  const handleUnholdAccount = async (user) => {
    try {
      await update(ref(database, `users/${user.facilityId}/${user.uid}`), {
        accountHeld: false,
        unheldAt: new Date().toISOString(),
        unheldBy: 'superadmin'
      });
      setApprovedUsers(approvedUsers.map(u => 
        u.uid === user.uid ? { ...u, accountHeld: false } : u
      ));
      openNotification('success', `Account for ${user.username} has been unholded`);
    } catch (err) {
      openNotification('error', `Failed to unhold account: ${err.message}`);
    }
  };

  const getUserDropdownItems = (user) => [
    {
      key: user.accountHeld ? 'unhold' : 'hold',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HeatMapOutlined style={{ color: user.accountHeld ? 'var(--success-color)' : 'var(--error-color)' }} />
          <span>{user.accountHeld ? 'Unhold Account' : 'Hold Account'}</span>
        </div>
      ),
      onClick: () => user.accountHeld ? handleUnholdAccount(user) : handleHoldAccount(user)
    },
    {
      key: 'notification',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MessageOutlined style={{ color: 'var(--primary-color)' }} />
          <span>Send Notification</span>
        </div>
      ),
      onClick: () => handleSendNotification(user)
    }
  ];

  const pendingColumns = [
    {title: <span style={{ color: 'var(--lcolor)' }}>Username</span>, dataIndex: 'username', key: 'username', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    {title: <span style={{ color: 'var(--lcolor)' }}>Email</span>, dataIndex: 'email', key: 'email', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    {title: <span style={{ color: 'var(--lcolor)' }}>Phone</span>, dataIndex: 'phone', key: 'phone', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    {title: <span style={{ color: 'var(--lcolor)' }}>Facility ID</span>, dataIndex: 'facilityId', key: 'facilityId', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    {title: <span style={{ color: 'var(--lcolor)' }}>Created At</span>, dataIndex: 'createdAt', key: 'createdAt', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    {
      title: <span style={{ color: 'var(--lcolor)' }}>Action</span>,
      key: 'action',
      render: (_, record) => (
        <div>
          <Button type="primary" onClick={() => handleApprove(record)} style={{ marginRight: 8 }}>
            Approve
          </Button>
          <Popconfirm
            title={`Are you sure you want to reject ${record.username}?`}
            onConfirm={() => handleReject(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ type: 'primary' }}
          >
            <Button type="danger">Reject</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const approvedColumns = [
    { title: <span style={{ color: 'var(--lcolor)' }}>Username</span>, dataIndex: 'username', key: 'username', render: (text) => <span style={{ color: 'white !important' }}> yoo {text}</span> },
    { title: <span style={{ color: 'var(--lcolor)' }}>Email</span>, dataIndex: 'email', key: 'email', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    { title: <span style={{ color: 'var(--lcolor)' }}>Phone</span>, dataIndex: 'phone', key: 'phone', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    { title: <span style={{ color: 'var(--lcolor)' }}>Facility ID</span>, dataIndex: 'facilityId', key: 'facilityId', render: (text) => <span style={{ color: 'var(--lcolor)' }}>{text}</span> },
    { 
      title: <span style={{ color: 'var(--lcolor)' }}>Role</span>, 
      dataIndex: 'role', 
      key: 'role',
      render: (role, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: 'var(--lcolor)' }}>{role}</span>
          {record.accountHeld && (
            <Badge status="error" text="Held" style={{ fontSize: '11px' }} />
          )}
        </div>
      )
    },
    {
      title: <span style={{ color: 'var(--lcolor)' }}>Action</span>,
      key: 'action',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handleVisitAdminPage(record)}
          >
            Visit
          </Button>
          <Button 
            type={record.accountHeld ? "default" : "danger"}
            size="small"
            onClick={() => record.accountHeld ? handleUnholdAccount(record) : handleHoldAccount(record)}
          >
            {record.accountHeld ? 'Unhold' : 'Hold'}
          </Button>
          <Button 
            type="primary" 
            size="small"
            icon={<MessageOutlined />}
            onClick={() => handleSendNotification(record)}
          >
            Message
          </Button>
          <Select
            defaultValue={record.role}
            onChange={(value) => handleChangeRole(record, value)}
            style={{ width: 120 }}
          >
            <Option value="admin">Admin</Option>
            <Option value="cashier">Cashier</Option>
            <Option value="superadmin">Super Admin</Option>
          </Select>
          <Popconfirm
            title={`Are you sure you want to delete ${record.username}?`}
            onConfirm={() => handleDeleteUser(record)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ type: 'primary' }}
          >
            <Button type="danger">Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="super-admin-container">
      {contextHolder}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: 'var(--lcolor)' }}>Super Admin Dashboard</h2>
        <SuperAdminMessaging facilities={approvedUsers} />
      </div>
      {loading ? (
        <Spin size="large" />
      ) : (
        <>
          <h3 style={{ color: 'var(--lcolor)' }}>
            Pending Users <Badge count={pendingCount} />
            <BellOutlined style={{ marginLeft: 8, color: 'var(--primary-color)', fontSize: 20 }} />
          </h3>
          <Radio.Group
            value={pendingViewMode}
            onChange={(e) => setPendingViewMode(e.target.value)}
            style={{ marginBottom: 20 }}
          >
            <Radio.Button value="table">Table View</Radio.Button>
            <Radio.Button value="card">Card View</Radio.Button>
          </Radio.Group>
          {pendingViewMode === 'table' ? (
            <Table
              columns={pendingColumns}
              dataSource={pendingUsers}
              rowKey="uid"
              pagination={false}
            />
          ) : (
            <div className="card-container">
              {pendingUsers.map((user) => (
                <Card key={user.uid} className="user-card">
                  <p><strong>Username:</strong> {user.username}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>Phone:</strong> {user.phone}</p>
                  <p><strong>Facility ID:</strong> {user.facilityId}</p>
                  <p><strong>Created At:</strong> {user.createdAt}</p>
                  <div className="card-actions">
                    <Button type="primary" onClick={() => handleApprove(user)}>
                      Approve
                    </Button>
                    <Popconfirm
                      title={`Are you sure you want to reject ${user.username}?`}
                      onConfirm={() => handleReject(user)}
                      okText="Yes"
                      cancelText="No"
                      okButtonProps={{ type: 'primary' }}
                    >
                      <Button danger>Reject</Button>
                    </Popconfirm>
                  </div>
                </Card>
              ))}
            </div>
          )}
          <h3 style={{ marginTop: 40, color: 'var(--lcolor)' }}>Approved Users</h3>
          <Radio.Group
            value={approvedViewMode}
            onChange={(e) => setApprovedViewMode(e.target.value)}
            style={{ marginBottom: 20 }}
          >
            <Radio.Button value="table">Table View</Radio.Button>
            <Radio.Button value="card">Card View</Radio.Button>
          </Radio.Group>
          {approvedViewMode === 'table' ? (
            <Table
              columns={approvedColumns}
              dataSource={approvedUsers}
              rowKey="uid"
              pagination={false}
            />
          ) : (
            <div className="card-container">
              {approvedUsers.map((user) => (
                <Card 
                  key={user.uid} 
                  className="user-card"
                  extra={
                    <Dropdown
                      menu={{ items: getUserDropdownItems(user) }}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <Button 
                        type="text" 
                        icon={<MoreOutlined />} 
                        style={{ 
                          border: 'none',
                          boxShadow: 'none',
                          color: 'var(--text-secondary)'
                        }}
                      />
                    </Dropdown>
                  }
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}><strong>{user.username}</strong></p>
                      {user.accountHeld && (
                        <Badge status="error" text="Account Held" style={{ fontSize: '12px' }} />
                      )}
                    </div>
                  </div>
                  <p style={{ margin: '4px 0', color: 'var(--lcolor)' }}><strong>Email:</strong> {user.email}</p>
                  <p style={{ margin: '4px 0', color: 'var(--lcolor)' }}><strong>Phone:</strong> {user.phone}</p>
                  <p style={{ margin: '4px 0', color: 'var(--lcolor)' }}><strong>Facility:</strong> {user.facilityId}</p>
                  <p style={{ margin: '4px 0', color: 'var(--lcolor)' }}><strong>Role:</strong> {user.role}</p>
                  <div className="card-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => handleVisitAdminPage(user)}
                    >
                      Visit Store
                    </Button>
                    <Select
                      defaultValue={user.role}
                      onChange={(value) => handleChangeRole(user, value)}
                      style={{ width: 120 }}
                    >
                      <Option value="admin">Admin</Option>
                      <Option value="cashier">Cashier</Option>
                    </Select>
                    <Popconfirm
                      title={`Are you sure you want to delete ${user.username}?`}
                      onConfirm={() => handleDeleteUser(user)}
                      okText="Yes"
                      cancelText="No"
                      okButtonProps={{ type: 'primary' }}
                    >
                      <Button danger>Delete Account</Button>
                    </Popconfirm>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Todos Section */}
          <h3 style={{ marginTop: 40, color: 'var(--lcolor)' }}>Todo List ({todos.filter(t => t.status === 'pending').length} pending)</h3>
          {todos.length === 0 ? (
            <Card>
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                No todos yet. Add notifications to your todo list from the notification bell.
              </p>
            </Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {todos.map((todo) => (
                <Card
                  key={todo.id}
                  size="small"
                  title={todo.title}
                  extra={
                    <Badge 
                      status={todo.status === 'completed' ? 'success' : 'processing'} 
                      text={todo.status === 'completed' ? 'Completed' : 'Pending'}
                    />
                  }
                  actions={
                    todo.status === 'pending' ? [
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleCompleteTodo(todo.id)}
                      >
                        Complete
                      </Button>,
                      <Popconfirm
                        title="Are you sure you want to delete this todo?"
                        onConfirm={() => handleDeleteTodo(todo.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button danger size="small">Delete</Button>
                      </Popconfirm>
                    ] : [
                      <Popconfirm
                        title="Remove this completed todo?"
                        onConfirm={() => handleDeleteTodo(todo.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button size="small">Remove</Button>
                      </Popconfirm>
                    ]
                  }
                >
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {todo.description}
                  </p>
                  <p style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                    Created: {new Date(todo.createdAt).toLocaleDateString()}
                    {todo.completedAt && (
                      <>
                        <br />
                        Completed: {new Date(todo.completedAt).toLocaleDateString()}
                      </>
                    )}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
      
      {/* Hold Account Modal || soon to add free trial feature*/}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HeatMapOutlined style={{ color: 'var(--error-color)' }} />
          <span>Hold Account</span>
        </div>}
        open={holdModalVisible}
        onCancel={() => setHoldModalVisible(false)}
        onOk={confirmHoldAccount}
        okText="Hold Account"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', fontSize: '16px' }}>
            Are you sure you want to hold the account for <strong>{selectedUser?.username}</strong>?
          </p>
          <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
            This will prevent the user from accessing their account and performing any operations.
          </p>
          <Checkbox 
            checked={confirmHold}
            onChange={(e) => setConfirmHold(e.target.checked)}
            style={{ fontSize: '14px' }}
          >
            I confirm that I want to hold this account
          </Checkbox>
        </div>
      </Modal>

      {/* Send Notification Modal */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MessageOutlined style={{ color: 'var(--primary-color)' }} />
          <span>Send Notification</span>
        </div>}
        open={notificationModalVisible}
        onCancel={() => setNotificationModalVisible(false)}
        onOk={confirmSendNotification}
        okText="Send Notification"
        cancelText="Cancel"
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', fontSize: '16px' }}>
            Send a notification to <strong>{selectedUser?.username}</strong>
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Title:</label>
            <Input 
              placeholder="e.g., Payment Issue, New Promotion, Account Update..."
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              style={{ marginBottom: '16px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Message:</label>
            <Input.TextArea 
              placeholder="Your message to the user..."
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              rows={4}
              showCount
              maxLength={500}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SuperAdmin;